import React, {useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import Context from '@s-ui/react-context'

const HEADING_TAGS = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  p: 'p'
}

const SIZES = {
  xxl: 'xxl',
  xl: 'xl',
  l: 'l',
  m: 'm',
  xss: 'xs-s',
  s: 's',
  xs: 'xs',
  xxs: 'xxs',
  xxxs: 'xxxs'
}

const COLORS = {
  black: 'black',
  currentColor: 'currentColor',
  gray: 'gray',
  white: 'white'
}

const TEXT_ALIGN = {
  left: 'left',
  right: 'right',
  center: 'center'
}

const BASE_CLASS = 'mt-TitleBasic'
const TITLE_CLASS = `${BASE_CLASS}-title`
const PRE_TITLE_CLASS = `${BASE_CLASS}-pre`
const DESCRIPTION_CLASS = `${BASE_CLASS}-desc`

const TitleBasic = ({
  as: Heading = HEADING_TAGS.h1,
  size = SIZES.l,
  color = COLORS.black,
  descriptionColor = COLORS.black,
  descriptionTextAlign,
  descriptionSize,
  icon: Icon,
  id,
  title,
  preTitle,
  description,
  textAlign = TEXT_ALIGN.left,
  children,
  secure = false,
  maxTitleLines
}) => {
  const {i18n} = useContext(Context)

  const hasIcon = Boolean(Icon)
  const preTitleClassName = cx(
    PRE_TITLE_CLASS,
    `${PRE_TITLE_CLASS}--${size}`,
    color === COLORS.white && `${PRE_TITLE_CLASS}--white`
  )
  const headingClassName = cx(TITLE_CLASS, `${TITLE_CLASS}--${size}`, `${TITLE_CLASS}--${color}`, {
    [`${TITLE_CLASS}--maxLines-${maxTitleLines}`]: Boolean(maxTitleLines)
  })

  const headingWrapperClassName = cx(
    `${TITLE_CLASS}Wrapper`,
    `${TITLE_CLASS}Wrapper--${color}`,
    textAlign && `${TITLE_CLASS}Wrapper--${textAlign}`
  )

  const descriptionClassName = cx(
    DESCRIPTION_CLASS,
    `${DESCRIPTION_CLASS}--${descriptionSize || size}`,
    `${DESCRIPTION_CLASS}--${descriptionColor}`,
    descriptionTextAlign && `${DESCRIPTION_CLASS}--${descriptionTextAlign}`
  )

  const renderText = text => {
    return secure ? text : htmlStringToReactElement(text)
  }

  return (
    <>
      {preTitle ? <p className={preTitleClassName}>{renderText(i18n.t(preTitle))}</p> : null}
      {title ? (
        <div className={headingWrapperClassName}>
          {hasIcon && (
            <div className={`${TITLE_CLASS}Icon`}>
              <Icon />
            </div>
          )}
          <Heading className={headingClassName} {...(id ? {id} : {})}>
            {renderText(i18n.t(title))}
          </Heading>
        </div>
      ) : null}
      {description ? <p className={descriptionClassName}>{renderText(i18n.t(description))}</p> : null}
      {children}
    </>
  )
}

TitleBasic.displayName = 'TitleBasic'
TitleBasic.propTypes = {
  as: PropTypes.oneOf(Object.values(HEADING_TAGS)),
  children: PropTypes.node,
  color: PropTypes.oneOf(Object.values(COLORS)),
  description: PropTypes.string,
  descriptionColor: PropTypes.oneOf(Object.values(COLORS)),
  descriptionSize: PropTypes.oneOf(Object.values(SIZES)),
  descriptionTextAlign: PropTypes.oneOf(Object.values(TEXT_ALIGN)),
  icon: PropTypes.node,
  id: PropTypes.string,
  maxTitleLines: PropTypes.number,
  preTitle: PropTypes.string,
  secure: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(SIZES)),
  textAlign: PropTypes.oneOf(Object.values(TEXT_ALIGN)),
  title: PropTypes.string
}

export {
  HEADING_TAGS as titleBasicTags,
  SIZES as titleBasicSizes,
  COLORS as titleBasicColors,
  TEXT_ALIGN as titleBasicTextAlign
}
export default TitleBasic
